import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { PATH_HOME } from "../constants/Paths";

/**
 * Page Reload Handler Hook
 * Detects page reloads and redirects to agent page if user state is lost
 * Similar to how ProductType redirects back to Agent Page after reload
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether reload detection is enabled (default: true)
 * @param {string} options.redirectPath - Where to redirect on reload (default: PATH_HOME)
 * @param {Array<string>} options.requiredStates - Redux states that must be present
 */
export const usePageReloadHandler = (options = {}) => {
  const {
    enabled = true,
    redirectPath = PATH_HOME,
    requiredStates = ['sessionToken', 'userProfile', 'agent']
  } = options;

  const navigate = useNavigate();
  
  // Get required Redux states
  const sessionToken = useSelector((state) => state.sessionToken);
  const userProfile = useSelector((state) => state.userProfile);
  const agent = useSelector((state) => state.agent);

  const stateMap = {
    sessionToken,
    userProfile,
    agent
  };

  useEffect(() => {
    if (!enabled) return;

    // Check if this is a page reload by looking for the performance navigation API
    const isReload = () => {
      // Method 1: Performance Navigation API (modern browsers)
      if (performance?.navigation?.type === performance.navigation.TYPE_RELOAD) {
        return true;
      }
      
      // Method 2: Performance Navigation Timing API (newer approach)
      if (performance?.getEntriesByType) {
        const navEntries = performance.getEntriesByType('navigation');
        if (navEntries.length > 0 && navEntries[0].type === 'reload') {
          return true;
        }
      }

      // Method 3: Check if page was accessed directly (no referrer from same origin)
      if (!document.referrer || !document.referrer.includes(window.location.origin)) {
        // This might be a direct access or external link, check if we have required state
        const missingStates = requiredStates.filter(state => !stateMap[state]);
        return missingStates.length > 0;
      }

      return false;
    };

    // Check for missing required states on mount
    const missingStates = requiredStates.filter(state => !stateMap[state]);
    
    if (missingStates.length > 0) {
      console.warn(`[PageReloadHandler] Missing required states: ${missingStates.join(', ')}`);
      
      if (isReload()) {
        console.warn('[PageReloadHandler] Page reload detected with missing state, redirecting to agent page');
        
        // Add a small delay to prevent immediate redirect loops
        setTimeout(() => {
          try {
            navigate(redirectPath, { replace: true });
          } catch (navError) {
            console.error('Navigation error in PageReloadHandler:', navError);
            // Force redirect as fallback
            window.location.href = redirectPath;
          }
        }, 100);
      }
    }
  }, [
    enabled,
    redirectPath,
    requiredStates,
    sessionToken,
    userProfile,
    agent,
    navigate
  ]);

  // Return state info for debugging
  return {
    hasRequiredStates: requiredStates.every(state => !!stateMap[state]),
    missingStates: requiredStates.filter(state => !stateMap[state]),
    stateMap
  };
};

/**
 * Hook for pages that need full authentication state on reload
 */
export const useFullStateReloadHandler = () => {
  return usePageReloadHandler({
    requiredStates: ['sessionToken', 'userProfile', 'agent']
  });
};

/**
 * Hook for pages that only need session state on reload
 */
export const useSessionReloadHandler = () => {
  return usePageReloadHandler({
    requiredStates: ['sessionToken']
  });
};

/**
 * Hook to disable reload protection (for login/home pages)
 */
export const useNoReloadHandler = () => {
  return usePageReloadHandler({
    enabled: false
  });
};